import clsx from 'clsx'
import {
  Activity,
  CheckCircle,
  ClockIcon,
  type LucideIcon,
  MinusCircleIcon,
  XCircle,
} from 'lucide-react'
import { type FC } from 'react'

import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'

export type TaskStatusStats = {
  total?: number
  completed?: number
  pending?: number
  processing?: number
  failed?: number
  excluded?: number
  canceled?: number
}

type TaskStatusFilterCardsProps = {
  stats: TaskStatusStats
  selectedStatuses: string[]
  onToggleStatus: (status: string) => void
  isLoading?: boolean
}

type StatusCardConfig = {
  status: keyof TaskStatusStats
  label: string
  icon: LucideIcon
  colorClasses: {
    border: string
    bgSelected: string
    bgHover: string
    textSelected: string
    textDefault: string
    textHover: string
    countSelected: string
    countDefault: string
    countHover: string
  }
  isClickable: boolean
}

type StatusCardProps = {
  config: StatusCardConfig
  count: string
  isSelected: boolean
  onToggleStatus?: (status: string) => void
}

const StatusCard: FC<StatusCardProps> = ({
  config,
  count,
  isSelected,
  onToggleStatus,
}) => {
  const { status, label, icon: Icon, colorClasses, isClickable } = config

  const cardClasses = clsx(
    'border',
    colorClasses.border,
    'transition-all',
    'w-[200px]',
    {
      'cursor-pointer': isClickable,
      [colorClasses.bgSelected]: isSelected,
      'shadow-lg': isSelected,
      'bg-white': !isSelected,
      [colorClasses.bgHover]: !isSelected,
      'hover:shadow-md': isClickable,
    }
  )

  const titleClasses = clsx(
    'text-sm',
    'font-medium',
    'flex',
    'items-center',
    'gap-2',
    'truncate',
    'transition-colors',
    {
      [colorClasses.textSelected]: isSelected,
      [colorClasses.textDefault]: !isSelected,
      [colorClasses.textHover]: !isSelected,
    }
  )

  const countClasses = clsx(
    'text-2xl',
    'font-bold',
    'transition-colors',
    {
      [colorClasses.countSelected]: isSelected,
      [colorClasses.countDefault]: !isSelected,
      [colorClasses.countHover]: !isSelected,
    }
  )

  return (
    <Card
      className={cardClasses}
      onClick={isClickable ? () => onToggleStatus?.(status) : undefined}
    >
      <CardHeader className='pb-2'>
        <CardTitle className={titleClasses}>
          <Icon className='w-4 h-4 flex-shrink-0' />
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className='pt-0'>
        <div className={countClasses}>{count}</div>
      </CardContent>
    </Card>
  )
}

const TaskStatusFilterCards: FC<TaskStatusFilterCardsProps> = ({
  stats,
  selectedStatuses,
  onToggleStatus,
  isLoading = false,
}) => {
  const getCount = (status: keyof TaskStatusStats): string => {
    if (isLoading) return '--'
    return stats[status] !== undefined ? String(stats[status] || 0) : '--'
  }

  const getTotalCount = (): string => {
    if (isLoading) return '--'
    return stats.total !== undefined ? String(stats.total || 0) : '--'
  }

  const isSelected = (status: string) => selectedStatuses.includes(status)

  const cardConfigs: StatusCardConfig[] = [
    {
      status: 'total',
      label: 'Total Tasks',
      icon: Activity,
      colorClasses: {
        border: 'border-purple-300',
        bgSelected: '',
        bgHover: '',
        textSelected: 'text-purple-400',
        textDefault: 'text-purple-400',
        textHover: '',
        countSelected: 'text-purple-500',
        countDefault: 'text-purple-500',
        countHover: '',
      },
      isClickable: false,
    },
    {
      status: 'completed',
      label: 'Completed',
      icon: CheckCircle,
      colorClasses: {
        border: 'border-green-300',
        bgSelected: 'bg-green-300',
        bgHover: 'hover:bg-green-50',
        textSelected: 'text-green-800',
        textDefault: 'text-green-400',
        textHover: 'hover:text-green-600',
        countSelected: 'text-green-800',
        countDefault: 'text-green-500',
        countHover: 'hover:text-green-700',
      },
      isClickable: true,
    },
    {
      status: 'pending',
      label: 'Pending',
      icon: ClockIcon,
      colorClasses: {
        border: 'border-yellow-300',
        bgSelected: 'bg-yellow-300',
        bgHover: 'hover:bg-yellow-50',
        textSelected: 'text-yellow-800',
        textDefault: 'text-yellow-400',
        textHover: 'hover:text-yellow-600',
        countSelected: 'text-yellow-800',
        countDefault: 'text-yellow-500',
        countHover: 'hover:text-yellow-700',
      },
      isClickable: true,
    },
    {
      status: 'processing',
      label: 'Processing',
      icon: Activity,
      colorClasses: {
        border: 'border-blue-300',
        bgSelected: 'bg-blue-300',
        bgHover: 'hover:bg-blue-50',
        textSelected: 'text-blue-800',
        textDefault: 'text-blue-400',
        textHover: 'hover:text-blue-600',
        countSelected: 'text-blue-800',
        countDefault: 'text-blue-500',
        countHover: 'hover:text-blue-700',
      },
      isClickable: true,
    },
    {
      status: 'failed',
      label: 'Failed',
      icon: XCircle,
      colorClasses: {
        border: 'border-red-300',
        bgSelected: 'bg-red-300',
        bgHover: 'hover:bg-red-50',
        textSelected: 'text-red-800',
        textDefault: 'text-red-400',
        textHover: 'hover:text-red-600',
        countSelected: 'text-red-800',
        countDefault: 'text-red-500',
        countHover: 'hover:text-red-700',
      },
      isClickable: true,
    },
    {
      status: 'excluded',
      label: 'Excluded',
      icon: MinusCircleIcon,
      colorClasses: {
        border: 'border-gray-300',
        bgSelected: 'bg-gray-300',
        bgHover: 'hover:bg-gray-50',
        textSelected: 'text-gray-800',
        textDefault: 'text-gray-400',
        textHover: 'hover:text-gray-600',
        countSelected: 'text-gray-800',
        countDefault: 'text-gray-500',
        countHover: 'hover:text-gray-700',
      },
      isClickable: true,
    },
  ]

  return (
    <div className='flex flex-wrap justify-start gap-4'>
      {cardConfigs.map((config) => {
        const count = config.status === 'total' ? getTotalCount() : getCount(config.status)
        return (
          <StatusCard
            key={config.status}
            config={config}
            count={count}
            isSelected={isSelected(config.status)}
            onToggleStatus={config.isClickable ? onToggleStatus : undefined}
          />
        )
      })}
    </div>
  )
}

export default TaskStatusFilterCards
